import { useEffect, useMemo, useState } from "react";
import { IRestaurantDetail } from "../models/IRestaurantDetail";
import { getRestaurantList } from "../services/restaurant";
import {
  getUserRestaurantLinks, IUserRestaurantLink, linkUserToRestaurant, setRestaurantUserLinkStatus,
} from "../services/restaurantUser";

const ERROR_TEXT: Record<string, string> = {
  RestourantUserAlreadyLinked: "Kullanıcı bu restorana zaten bağlı (kapalı bağ varsa listeden açın).",
  SystemUserNotModifiable: "Sistem hesapları restorana bağlanamaz.",
  RestourantAccessDenied: "Bu restoranda işlem yapma yetkiniz yok.",
  RestourantUserNotFound: "Bağ bulunamadı. Listeyi yenileyin.",
};

function errorText(err: any, fallback: string): string {
  if (err?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  const data = err?.response?.data;
  const code = Array.isArray(data) ? data[0]?.code : undefined;
  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
  if (Array.isArray(data) && data[0]?.description) return data[0].description;
  return fallback;
}

interface Props {
  userId: string;
  userName: string;
  /** Bağ değişince kullanıcı listesindeki "bağlı restoran" bilgisi tazelensin diye. */
  onChanged?: () => void;
}

/**
 * Bir hesap birden çok restorana bağlanabilir (aynı e-posta/telefon, tek hesap).
 * Bağ silinmez; kapatılır. Roller hesap başınadır: kullanıcı bağlı olduğu her restoranda aynı roldedir.
 */
export default function UserRestaurantLinks({ userId, userName, onChanged }: Props) {
  const [links, setLinks] = useState<IUserRestaurantLink[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [restaurants, setRestaurants] = useState<IRestaurantDetail[] | null>(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState<string | null>(null); // "link" ya da bağ id'si
  const [confirmCloseId, setConfirmCloseId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadLinks = () =>
    getUserRestaurantLinks(userId)
      .then((res) => { setLinks(Array.isArray(res.data) ? res.data : []); setLoadError(""); })
      .catch((err) => {
        const code = Array.isArray(err?.response?.data) ? err.response.data[0]?.code : undefined;
        // Hiç bağ yoksa uç 404 döner; bu boş liste demektir.
        if (err?.response?.status === 404 || code === "RestourantUserNotFound") { setLinks([]); return; }
        setLinks(null);
        setLoadError("Restoran bağları alınamadı.");
      });

  useEffect(() => {
    loadLinks();
    getRestaurantList()
      .then((res) => setRestaurants(res.data?.items ?? []))
      .catch(() => setRestaurants([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const nameOf = (link: IUserRestaurantLink) =>
    link.restourants?.restaurantName
    ?? restaurants?.find((r) => r.id?.toLowerCase() === link.restourantId.toLowerCase())?.restourantName
    ?? "Restoran";

  const sortedLinks = useMemo(
    () => (links ?? []).slice().sort((a, b) =>
      Number(b.restourantUserIsEnabled) - Number(a.restourantUserIsEnabled) ||
      nameOf(a).localeCompare(nameOf(b), "tr")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [links, restaurants]
  );

  // Eklenebilir: hiç bağı olmayan restoranlar (kapalı bağ da bağdır; o listeden açılır).
  const linkedIds = useMemo(() => new Set((links ?? []).map((l) => l.restourantId.toLowerCase())), [links]);
  const addable = useMemo(
    () => (restaurants ?? [])
      .filter((r) => r.id && !linkedIds.has(r.id.toLowerCase()))
      .sort((a, b) => (a.restourantName ?? "").localeCompare(b.restourantName ?? "", "tr")),
    [restaurants, linkedIds]
  );

  const reset = () => { setError(""); setSuccess(""); };

  const link = async () => {
    if (!selected) return;
    const name = restaurants?.find((r) => r.id === selected)?.restourantName ?? "Restoran";
    setBusy("link");
    reset();
    try {
      await linkUserToRestaurant(userId, selected);
      setSuccess(`${name} restoranına bağlandı.`);
      setSelected("");
      await loadLinks();
      onChanged?.();
    } catch (err) {
      setError(errorText(err, "Restorana bağlanamadı."));
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (l: IUserRestaurantLink, enable: boolean) => {
    setBusy(l.id);
    reset();
    try {
      await setRestaurantUserLinkStatus(l.id, enable);
      setSuccess(`${nameOf(l)} bağı ${enable ? "açıldı" : "kapatıldı"}.`);
      setConfirmCloseId(null);
      await loadLinks();
      onChanged?.();
    } catch (err) {
      setError(errorText(err, enable ? "Bağ açılamadı." : "Bağ kapatılamadı."));
    } finally {
      setBusy(null);
    }
  };

  const activeCount = (links ?? []).filter((l) => l.restourantUserIsEnabled).length;

  return (
    <div className="url">
      <div className="url__head">
        <span><i className="bi bi-shop me-2"></i>Restoranlar</span>
        {links && <span className="url__count">{activeCount} aktif</span>}
      </div>

      {loadError ? (
        <div className="ura__msg ura__msg--error"><i className="bi bi-exclamation-circle-fill"></i>{loadError}</div>
      ) : !links ? (
        <div className="ura__loading"><span className="spinner-border spinner-border-sm" />Yükleniyor…</div>
      ) : sortedLinks.length === 0 ? (
        <div className="ura__empty">Kullanıcı hiçbir restorana bağlı değil.</div>
      ) : (
        <div className="url__list">
          {sortedLinks.map((l) => {
            const confirming = confirmCloseId === l.id;
            const lastActive = l.restourantUserIsEnabled && activeCount === 1;
            return (
              <div key={l.id} className={`url__row${l.restourantUserIsEnabled ? "" : " url__row--off"}${confirming ? " url__row--confirm" : ""}`}>
                {confirming ? (
                  <>
                    <span className="url__confirm">
                      <strong>{nameOf(l)}</strong> bağı kapatılsın mı?
                      {lastActive && <em> Bu son aktif restoranı; kullanıcı panele giriş yapamaz hale gelebilir.</em>}
                    </span>
                    <div className="url__actions">
                      <button type="button" className="ura__link" disabled={busy !== null} onClick={() => setConfirmCloseId(null)}>Vazgeç</button>
                      <button type="button" className="ura__btn ura__btn--danger ura__btn--sm" disabled={busy !== null} onClick={() => setStatus(l, false)}>
                        {busy === l.id ? <span className="spinner-border spinner-border-sm" /> : "Evet, kapat"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="url__name">
                      <i className={`bi ${l.restourantUserIsEnabled ? "bi-check-circle-fill url__ok" : "bi-dash-circle url__off"} me-2`}></i>
                      {nameOf(l)}
                    </span>
                    {l.restourantUserIsEnabled ? (
                      <button type="button" className="ura__link ura__link--danger" disabled={busy !== null}
                        onClick={() => { setConfirmCloseId(l.id); reset(); }}>Kapat</button>
                    ) : (
                      <button type="button" className="ura__link" disabled={busy !== null} onClick={() => setStatus(l, true)}>
                        {busy === l.id ? <span className="spinner-border spinner-border-sm" /> : "Aç"}
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {links && (
        <div className="url__add">
          <div className="ura__sub">Restoran ekle</div>
          {restaurants === null ? (
            <div className="ura__loading"><span className="spinner-border spinner-border-sm" />Restoranlar yükleniyor…</div>
          ) : addable.length === 0 ? (
            <div className="ura__empty">Bağlanabilecek başka restoran yok.</div>
          ) : (
            <div className="ura__row">
              <select className="form-select ura__select" value={selected} disabled={busy !== null}
                onChange={(e) => { setSelected(e.target.value); reset(); }}>
                <option value="">Restoran seçin…</option>
                {addable.map((r) => <option key={r.id} value={r.id!}>{r.restourantName}</option>)}
              </select>
              <button type="button" className="ura__btn" disabled={!selected || busy !== null} onClick={link}>
                {busy === "link" ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-link-45deg"></i>Bağla</>}
              </button>
            </div>
          )}
          <div className="ura__desc">
            {userName || "Kullanıcı"} aynı hesapla eklenen her restoranı yönetebilir; restoranlar arasında Dashboard'dan geçer.
            Roller hesap başınadır — kullanıcı bağlı olduğu her restoranda aynı roldedir.
          </div>
        </div>
      )}

      {error && <div className="ura__msg ura__msg--error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}
      {success && <div className="ura__msg ura__msg--ok"><i className="bi bi-check-circle-fill"></i>{success}</div>}
    </div>
  );
}
